import { CheckCircleIcon, ExclamationCircleIcon, PlayCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import DashboardMenu from "containers/Admin/DashboardPage/DashboardMenu";
import { AnimatePresence, motion } from "framer-motion";
import React, { useRef, useState } from "react";
import { EASING } from "../../../../utils/easingUtils";

interface ApiResponse {
  message: string;
  details: string[];
}

const PriceMigrationDashboard: React.FC = () => {
  const server = process.env.REACT_APP_SERVER_URL || "";

  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const handleRunMigration = async () => {
    setIsLoading(true);
    setMessages([]);
    setError(null);

    try {
      const response = await fetch(`${server}/api/price/copy`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error("Échec de l'exécution de la migration");
      }

      const data: ApiResponse = await response.json();
      setMessages(data.details);
      if (data.message.includes("erreurs")) {
        setError("Migration terminée avec des problèmes. Vérifiez les détails ci-dessous.");
      }
    } catch (err) {
      setError("Échec de la connexion au serveur. Veuillez réessayer.");
      setMessages([err instanceof Error ? err.message : "Erreur inconnue"]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <DashboardMenu>
        <div className="flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="w-full p-6 bg-white shadow-xl rounded-xl">
            {/* En-tête */}
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold text-gray-800">Migration des prix</h1>
              <motion.button
                onClick={handleRunMigration}
                disabled={isLoading}
                className={`flex items-center px-4 py-2 rounded-lg text-white font-semibold transition-all duration-200 ${
                  isLoading ? "bg-gray-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700"
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <PlayCircleIcon className="w-5 h-5 mr-2" />
                {isLoading ? "En cours..." : "Lancer la migration"}
              </motion.button>
            </div>

            {/* Message d'erreur */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center p-4 mb-4 text-red-800 bg-red-100 rounded-lg"
              >
                <ExclamationCircleIcon className="w-5 h-5 mr-2" />
                <span>{error}</span>
              </motion.div>
            )}

            {/* Journal des messages */}
            <div className="p-4 overflow-y-auto rounded-lg bg-gray-50 h-96">
              {isLoading && messages.length === 0 ? (
                // Animation de réflexion Grok
                <div className="flex flex-col items-center justify-center h-full space-y-4">
                  <div className="flex space-x-2">
                    {[...Array(3)].map((_, index) => (
                      <motion.div
                        key={index}
                        className="w-3 h-3 bg-indigo-600 rounded-full"
                        animate={{
                          scale: [1, 1.5, 1],
                          opacity: [0.5, 1, 0.5],
                        }}
                        transition={{
                          duration: 0.8,
                          repeat: Infinity,
                          delay: index * 0.2,
                          ease: EASING.easeInOut,
                        }}
                      />
                    ))}
                  </div>
                  <p className="text-sm text-gray-600">Migration en cours de traitement...</p>
                </div>
              ) : messages.length === 0 ? (
                // Espace réservé
                <p className="text-center text-gray-500">
                  Aucun message pour le moment. Lancez la migration pour voir les résultats.
                </p>
              ) : (
                // Messages
                <AnimatePresence>
                  {messages.map((msg, index) => {
                    const isSuccess = msg.startsWith("Successfully") || msg.includes("No duplicate");
                    const isSkipped = msg.startsWith("Skipped");
                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.4, ease: EASING.easeOut }}
                        className="flex items-center p-2 mb-2 text-sm text-gray-700 bg-white rounded-lg shadow-sm"
                      >
                        {isSuccess && <CheckCircleIcon className="w-5 h-5 mr-2 text-green-500" />}
                        {isSkipped && <XCircleIcon className="w-5 h-5 mr-2 text-red-500" />}
                        {msg}
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
        </div>
      </DashboardMenu>
    </>
  );
};

export default PriceMigrationDashboard;
